import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getInboxMessagesDetailsSelector, getOrganizationInboxMessagesSelector } from '@redux/inboxMessages/inboxMessagesSelector';
import {
  clearSelectedInboxMessagesDetailsThunk,
  getInboxMessagesDetailsThunk,
  getInboxMessagesThunk,
  postInboxMessagesResponseThunk
} from '@redux/inboxMessages/inboxMessagesThunks';
import { getAuthPersonSelector } from '@redux/oauth/oauthSelectors';
import { getActiveOrganizationIdSelector } from '@redux/organization/organizationSelectors';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import NavTopBarContentWrapper from 'components/NavTopBarContentWrapper/NavTopBarContentWrapper';
import { NoRequestSelectedArea } from 'containers/Inbox/components/NoRequestSelectedArea/NoRequestSelectedArea';
import TaskInfoDrawer from 'containers/Tasks/components/TaskInfoDrawer/TaskInfoDrawer';
import { ConversationFeedArea } from './components/ConversationFeedArea/ConversationFeedArea';
import { ConversationsArea } from './components/ConversationsArea/ConversationsArea';
import style from './Inbox.scss';

type Props = ConnectedProps<typeof connectRedux>;
const Inbox: React.FC<Props> = ({
  authPersonSelector,
  activeOrganizationId,
  inboxMessages,
  inboxMessagesDetails,
  fetchInboxMessages,
  fetchInboxMessagesDetails,
  clearInboxMessagesDetails,
  postResponse
}) => {
  const organizationId = activeOrganizationId;
  const personId = authPersonSelector?.id ?? '';
  const [selectedInboxMessage, setSelectedInboxMessage] = useState<Nullable<string>>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const { showAlertInfo, showAlertWarning } = useAlert();

  const onSendResponse = async (reply: string, messageId: string) => {
    try {
      await postResponse({
        inboxMessageId: messageId,
        responderId: personId,
        response: reply
      });
      showAlertInfo({
        message: 'Response sent successfully!'
      });
    } catch (err) {
      showAlertWarning({
        message: 'Something went wrong sending your response. Please try again.'
      });
    }
  };

  const onSelectTaskSwitch = useCallback((id: string = '') => {
    setSelectedTaskId(id);
  }, []);

  useEffect(() => {
    const fetchInbox = async () => {
      ``;
      await fetchInboxMessages(organizationId);
    };

    fetchInbox();
  }, []);

  useEffect(() => {
    if (selectedInboxMessage) {
      if (inboxMessagesDetails) clearInboxMessagesDetails();

      fetchInboxMessagesDetails(selectedInboxMessage);
    }
  }, [selectedInboxMessage]);

  return (
    <NavTopBarContentWrapper contentClassName={style.contentWrapper}>
      <div className={style.inboxLayoutContainer}>
        <div className={style.conversationsAreaContainer}>
          <ConversationsArea
            inboxMessages={inboxMessages}
            selectedInboxMessage={selectedInboxMessage}
            onInboxMessageSelected={(inboxMessageId) => setSelectedInboxMessage(inboxMessageId)}
          ></ConversationsArea>
        </div>
        <div className={style.conversationFeedAreaContainer}>
          {!selectedInboxMessage && <NoRequestSelectedArea />}
          {!!selectedInboxMessage && !!inboxMessagesDetails && (
            <ConversationFeedArea inboxMessagesDetails={inboxMessagesDetails!} onSelectTaskSwitch={onSelectTaskSwitch} sendResponse={onSendResponse}></ConversationFeedArea>
          )}
        </div>
      </div>
      {selectedTaskId && <TaskInfoDrawer taskId={selectedTaskId} onClose={() => onSelectTaskSwitch()} />}
    </NavTopBarContentWrapper>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    authPersonSelector: getAuthPersonSelector(state),
    activeOrganizationId: getActiveOrganizationIdSelector(state),
    inboxMessages: getOrganizationInboxMessagesSelector(state),
    inboxMessagesDetails: getInboxMessagesDetailsSelector(state)
  }),
  {
    fetchInboxMessages: getInboxMessagesThunk,
    fetchInboxMessagesDetails: getInboxMessagesDetailsThunk,
    clearInboxMessagesDetails: clearSelectedInboxMessagesDetailsThunk,
    postResponse: postInboxMessagesResponseThunk
  }
);

export default compose(connectRedux)(Inbox);
